import { useState } from 'react';
import type {
  ScheduledPayment,
  ScheduledPaymentDirection,
  ScheduledPaymentInput,
  ScheduledPaymentStatus,
} from '../../lib/cashflowApi';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useCompanies } from '../../hooks/useCompanies';
import { useGlobalCandidates } from '../../hooks/useBankStatements';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { useI18n } from '../../contexts/I18nContext';

interface ScheduledPaymentFormModalProps {
  title: string;
  initial?: ScheduledPayment;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: ScheduledPaymentInput) => void | Promise<void>;
}

const STATUS_VALUES: ScheduledPaymentStatus[] = ['PLANNED', 'DONE', 'CANCELLED'];

type CounterpartyKind = 'supplier' | 'client';

/**
 * Create/edit modal for a planned payment (PAYMENT) or expected receipt
 * (RECEIPT) in the Cash flow submodule. Modeled on SubscriptionFormModal.
 * Status is only editable on edit — new entries are always PLANNED.
 */
export function ScheduledPaymentFormModal({
  title,
  initial,
  submitting,
  onClose,
  onSubmit,
}: ScheduledPaymentFormModalProps) {
  const { t } = useI18n();
  const [direction, setDirection] = useState<ScheduledPaymentDirection>(
    initial?.direction ?? 'PAYMENT',
  );
  const [name, setName] = useState(initial?.name ?? '');
  const [amount, setAmount] = useState(initial ? String(initial.amount) : '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'RON');
  // Bare YYYY-MM-DD for DateField; backend accepts it as-is.
  const [dueDate, setDueDate] = useState(initial ? initial.dueDate.slice(0, 10) : '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [status, setStatus] = useState<ScheduledPaymentStatus>(initial?.status ?? 'PLANNED');
  const [error, setError] = useState<string | null>(null);

  // Reconcile-style categorisation: counterparty (furnizor/client), project and
  // expense category, with the same candidate lists as the reconcile page.
  const { data: candidates } = useGlobalCandidates();
  const [kind, setKind] = useState<CounterpartyKind>(() => {
    if (initial?.clientId) return 'client';
    if (initial?.supplierId) return 'supplier';
    return (initial?.direction ?? 'PAYMENT') === 'RECEIPT' ? 'client' : 'supplier';
  });
  const [counterpartyId, setCounterpartyId] = useState(
    initial?.supplierId ?? initial?.clientId ?? '',
  );
  const [projectId, setProjectId] = useState(initial?.projectId ?? '');
  const [expenseCategoryId, setExpenseCategoryId] = useState(initial?.expenseCategoryId ?? '');

  const counterpartyOptions =
    kind === 'supplier'
      ? (candidates?.suppliers ?? []).map((s) => ({ value: s.id, label: s.name }))
      : (candidates?.clients ?? []).map((c) => ({ value: c.id, label: c.name }));

  // Real spend categories only — the "income"/"expense" placeholders are the
  // TYPE, not a category (same filter as the reconcile page).
  const categoryOptions = (candidates?.categories ?? [])
    .filter((c) => c.code !== 'income' && c.code !== 'expense')
    .map((c) => ({ value: c.id, label: c.name }));

  const projectOptions = (candidates?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.code ? `${p.code} · ${p.name}` : p.name,
  }));

  function switchKind(next: CounterpartyKind) {
    setKind(next);
    setCounterpartyId('');
  }

  function switchDirection(next: ScheduledPaymentDirection) {
    setDirection(next);
    // Follow the natural counterparty for the direction while nothing is picked.
    if (!counterpartyId) setKind(next === 'RECEIPT' ? 'client' : 'supplier');
  }

  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const activeCompanyId = useActiveCompanyId();
  const [companyId, setCompanyId] = useState<string>(
    initial ? (initial.companyId ?? '') : (activeCompanyId ?? ''),
  );

  const isCreate = !initial;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('cashflow.form.nameRequired'));
      return;
    }
    const parsedAmount = parseFloat(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError(t('cashflow.form.amountInvalid'));
      return;
    }
    if (!dueDate) {
      setError(t('cashflow.form.dueDateRequired'));
      return;
    }

    const input: ScheduledPaymentInput = {
      direction,
      name: name.trim(),
      amount: parsedAmount,
      currency: currency.trim() || 'RON',
      dueDate,
      notes: notes.trim() || null,
      supplierId: kind === 'supplier' ? counterpartyId || null : null,
      clientId: kind === 'client' ? counterpartyId || null : null,
      projectId: projectId || null,
      // Expense category only makes sense for money going out (like reconcile).
      expenseCategoryId: direction === 'PAYMENT' ? expenseCategoryId || null : null,
    };
    if (!isCreate) input.status = status;
    if (multiFirm) {
      if (initial) input.companyId = companyId === '' ? null : companyId;
      else if (companyId) input.companyId = companyId;
    }
    try {
      await onSubmit(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            {/* Direction toggle: payment (money out) vs receipt (money in). */}
            <div className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.direction')}
              </span>
              <div className="inline-flex rounded-md border border-gray-300 p-0.5">
                {(['PAYMENT', 'RECEIPT'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => switchDirection(d)}
                    className={`rounded px-3 py-1.5 text-sm font-medium ${
                      direction === d
                        ? d === 'PAYMENT'
                          ? 'bg-red-600 text-white'
                          : 'bg-emerald-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {d === 'PAYMENT'
                      ? t('cashflow.directionPayment')
                      : t('cashflow.directionReceipt')}
                  </button>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.form.name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.form.amount')} *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.form.currency')}
              </label>
              <input
                type="text"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                placeholder="RON"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.dueDate')} *
              </label>
              <DateField value={dueDate} onChange={setDueDate} className="w-full" />
            </div>

            {multiFirm && companies && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  {t('cashflow.form.company')}
                </span>
                <Select
                  label={t('cashflow.form.company')}
                  hideLabel
                  clearable
                  value={companyId}
                  onChange={setCompanyId}
                  placeholder={t('cashflow.form.defaultCompany')}
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.displayName || c.legalName,
                  }))}
                />
              </div>
            )}

            {!isCreate && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  {t('cashflow.form.status')}
                </span>
                <Select
                  label={t('cashflow.form.status')}
                  hideLabel
                  value={status}
                  onChange={(v) => setStatus(v as ScheduledPaymentStatus)}
                  options={STATUS_VALUES.map((s) => ({
                    value: s,
                    label: t(`cashflow.status.${s.toLowerCase()}`),
                  }))}
                />
              </div>
            )}

            {/* Counterparty: furnizor sau client, ca la reconcilierea tranzacțiilor. */}
            <div className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.counterpartyType')}
              </span>
              <div className="inline-flex rounded-md border border-gray-300 p-0.5">
                {(['supplier', 'client'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => switchKind(k)}
                    className={`rounded px-3 py-1.5 text-sm font-medium ${
                      kind === k ? 'bg-brand-600 text-white' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {t(`cash.counterparty.${k}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t(`cash.counterparty.${kind}`)}
              </span>
              <Select
                label={t(`cash.counterparty.${kind}`)}
                hideLabel
                searchable
                clearable
                value={counterpartyId}
                onChange={setCounterpartyId}
                placeholder="—"
                options={counterpartyOptions}
              />
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.project')}
              </span>
              <Select
                label={t('cash.project')}
                hideLabel
                searchable
                clearable
                value={projectId}
                onChange={setProjectId}
                placeholder="—"
                options={projectOptions}
              />
            </div>
            {direction === 'PAYMENT' && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  {t('cash.category')}
                </span>
                <Select
                  label={t('cash.category')}
                  hideLabel
                  searchable
                  clearable
                  value={expenseCategoryId}
                  onChange={setExpenseCategoryId}
                  placeholder="—"
                  options={categoryOptions}
                />
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cashflow.form.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
